/* Reef Lagoon — Social Proof slider ("What the Crew Says").
 * Progressive enhancement for sections/reef-social-proof.liquid. Same pattern/structure as
 * reef-tee-carousel.js, tuned for taller review cards:
 *   - manual advance ONLY (no autoplay): always-visible prev/next + dot indicators
 *   - native scroll-snap track (touch swipe works with zero JS)
 *   - off-screen cards are made inert so they don't trap keyboard focus
 *   - the whole section fades + rises once on scroll-in (transform/opacity)
 * All motion gated behind prefers-reduced-motion. Idempotent; re-inits on shopify:section:load. */
(function () {
  'use strict';

  var REDUCE = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var INERT_OK = 'inert' in HTMLElement.prototype;

  function clamp(n, lo, hi) { return Math.max(lo, Math.min(hi, n)); }

  function initSlider(root) {
    if (!root || root.dataset.rspInit === '1') return;
    root.dataset.rspInit = '1';

    var track = root.querySelector('[data-rsp-track]');
    if (!track) return;
    var cards = [].slice.call(track.querySelectorAll('[data-rsp-card]'));
    if (!cards.length) return;

    var prev = root.querySelector('[data-rsp-prev]');
    var next = root.querySelector('[data-rsp-next]');
    var dots = [].slice.call(root.querySelectorAll('[data-rsp-dot]'));
    var controls = root.querySelector('[data-rsp-controls]');
    var base = 0;

    /* ---- one-time section reveal (fade + rise) ---- */
    if (!REDUCE && 'IntersectionObserver' in window) {
      root.classList.add('rsp--observe');
      var rio = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) { root.classList.add('is-in'); rio.disconnect(); }
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
      rio.observe(root);
    } else {
      root.classList.add('is-in');
    }

    /* ---- helpers ---- */
    function cardLeft(c) { return c.offsetLeft - base; } // scroll position of a card's start
    function maxScroll() { return track.scrollWidth - track.clientWidth; }
    function hasOverflow() { return maxScroll() > 4; }

    function currentIndex() {
      var sl = track.scrollLeft, best = 0, bd = Infinity;
      for (var i = 0; i < cards.length; i++) {
        var d = Math.abs(cardLeft(cards[i]) - sl);
        if (d < bd) { bd = d; best = i; }
      }
      return best;
    }

    function cardVisible(c) {
      var cl = cardLeft(c), cr = cl + c.offsetWidth;
      var vl = track.scrollLeft, vr = vl + track.clientWidth;
      var overlap = Math.min(cr, vr) - Math.max(cl, vl);
      return overlap >= c.offsetWidth * 0.5;
    }

    // Keep off-screen cards out of the tab order / a11y tree (no focus trap).
    function setHidden(card, hidden) {
      if (hidden && card.contains(document.activeElement)) hidden = false; // never hide the focused card
      if (INERT_OK) {
        card.inert = hidden;
      } else {
        var f = [].slice.call(card.querySelectorAll('a,button,[tabindex]'));
        f.forEach(function (el) {
          if (hidden) {
            if (!el.hasAttribute('data-rsp-ti')) el.setAttribute('data-rsp-ti', el.getAttribute('tabindex') || '');
            el.setAttribute('tabindex', '-1');
          } else {
            var t = el.getAttribute('data-rsp-ti');
            if (t === '') el.removeAttribute('tabindex');
            else if (t !== null) el.setAttribute('tabindex', t);
            el.removeAttribute('data-rsp-ti');
          }
        });
      }
      card.setAttribute('aria-hidden', hidden ? 'true' : 'false');
    }

    function go(i) {
      i = clamp(i, 0, cards.length - 1);
      track.scrollTo({ left: cardLeft(cards[i]), behavior: REDUCE ? 'auto' : 'smooth' });
    }

    function update() {
      var sl = track.scrollLeft, max = maxScroll();
      if (prev) prev.disabled = sl <= 2;
      if (next) next.disabled = sl >= max - 2;
      var idx = currentIndex();
      dots.forEach(function (d, i) {
        var on = i === idx;
        d.classList.toggle('is-on', on);
        if (on) d.setAttribute('aria-current', 'true'); else d.removeAttribute('aria-current');
      });
      cards.forEach(function (c) { setHidden(c, !cardVisible(c)); });
    }

    function refresh() {
      base = cards[0].offsetLeft;
      var overflow = hasOverflow();
      if (controls) controls.hidden = !overflow;
      if (!overflow) { cards.forEach(function (c) { setHidden(c, false); }); return; }
      update();
    }

    /* ---- controls ---- */
    if (prev) prev.addEventListener('click', function () { go(currentIndex() - 1); });
    if (next) next.addEventListener('click', function () { go(currentIndex() + 1); });
    dots.forEach(function (d) {
      d.addEventListener('click', function () { go(parseInt(d.getAttribute('data-index'), 10) || 0); });
    });

    /* ---- scroll bookkeeping (rAF-throttled) ---- */
    var ticking = false;
    track.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () { update(); ticking = false; });
    }, { passive: true });

    window.addEventListener('resize', refresh);

    refresh();
  }

  function initAll() {
    var els = document.querySelectorAll('[data-rsp]');
    for (var i = 0; i < els.length; i++) initSlider(els[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var el = e.target && e.target.querySelector ? e.target.querySelector('[data-rsp]') : null;
    if (el) { initSlider(el); } else { initAll(); }
  });
})();
